import React, { useLayoutEffect, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { CheckCircle2, XCircle, MinusCircle, AlertCircle, Slash, X, Loader2, AlertTriangle } from 'lucide-react';
import {
  type BulkScrapeReport,
  type BulkScrapeRowResult,
  type BulkScrapeRowStatus,
  type StatFieldChange,
  analyzeSuspiciousStatChange,
  countSuspiciousRows,
  formatStatChangeCell,
  formatSuspiciousChangeHint,
  rowHasSuspiciousChanges,
  summarizeBulkScrapeReport,
  getBulkSaveSelectableRowIds,
} from '../../utils/bulkScrapeReport';

type FilterKey = 'all' | 'suspicious' | BulkScrapeRowStatus;

const STATUS_LABEL: Record<BulkScrapeRowStatus, string> = {
  updated: 'Kaydedilecek',
  skipped: 'Atlandı',
  unchanged: 'Tarih güncellenecek',
  failed: 'Hata',
  no_selector: 'Seçici yok',
};

const STATUS_LABEL_COMMITTED: Record<BulkScrapeRowStatus, string> = {
  updated: 'Kaydedildi',
  skipped: 'Atlandı',
  unchanged: 'Tarih güncellendi',
  failed: 'Hata',
  no_selector: 'Seçici yok',
};

const STATUS_CLASS: Record<BulkScrapeRowStatus, string> = {
  updated: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  skipped: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  unchanged: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  no_selector: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300',
};

type Props = {
  open: boolean;
  report: BulkScrapeReport | null;
  saving?: boolean;
  onClose: () => void;
  onConfirmSave?: (selectedRowIds?: string[]) => void | Promise<void>;
};

function StatChangeCell({ change }: { change: StatFieldChange | undefined }) {
  if (!change) {
    return <span className="text-gray-400">—</span>;
  }

  const text = formatStatChangeCell(change);
  const suspicious = analyzeSuspiciousStatChange(change);

  if (!suspicious) {
    return <span>{text}</span>;
  }

  return (
    <span
      className="inline-flex max-w-full flex-col gap-0.5 rounded-md border border-amber-400/80 bg-amber-50 px-1.5 py-1 text-amber-950 ring-1 ring-amber-400/30 dark:border-amber-500/60 dark:bg-amber-950/50 dark:text-amber-100 dark:ring-amber-500/25"
      title={formatSuspiciousChangeHint(suspicious)}
    >
      <span className="inline-flex items-center gap-1 font-semibold">
        <AlertTriangle size={12} className="shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
        {text}
      </span>
      <span className="text-[10px] font-medium leading-tight text-amber-800/90 dark:text-amber-200/90">
        {formatSuspiciousChangeHint(suspicious)}
      </span>
    </span>
  );
}

const BulkScrapeReportDialog: React.FC<Props> = ({
  open,
  report,
  saving = false,
  onClose,
  onConfirmSave,
}) => {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [saveOnlySelected, setSaveOnlySelected] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [selectionSessionKey, setSelectionSessionKey] = useState('');

  const summary = useMemo(
    () => (report ? summarizeBulkScrapeReport(report) : null),
    [report]
  );

  const suspiciousCount = useMemo(
    () => (report ? countSuspiciousRows(report.rows) : 0),
    [report]
  );

  const filteredRows = useMemo(() => {
    if (!report) return [];
    if (filter === 'all') return report.rows;
    if (filter === 'suspicious') return report.rows.filter(rowHasSuspiciousChanges);
    return report.rows.filter((r) => r.status === filter);
  }, [report, filter]);

  const isPreview = report?.mode === 'preview';

  const selectableIdList = useMemo(
    () => (report && isPreview ? getBulkSaveSelectableRowIds(report) : []),
    [report, isPreview]
  );

  const selectableIdSet = useMemo(() => new Set(selectableIdList), [selectableIdList]);

  const reportSessionKey = report
    ? `${report.mode}:${report.finishedAt.getTime()}:${report.rows.length}:${selectableIdList.length}`
    : '';

  const selectedCount = useMemo(() => {
    let n = 0;
    for (const id of selectedIds) if (selectableIdSet.has(id)) n += 1;
    return n;
  }, [selectedIds, selectableIdSet]);

  const visibleSelectableIds = useMemo(
    () => filteredRows.filter((r) => selectableIdSet.has(r.id)).map((r) => r.id),
    [filteredRows, selectableIdSet]
  );

  useLayoutEffect(() => {
    if (!open) {
      setSaveOnlySelected(true);
      setSelectedIds(new Set());
      setSelectionSessionKey('');
      setFilter('all');
      return;
    }
    if (
      report?.mode === 'preview' &&
      reportSessionKey &&
      reportSessionKey !== selectionSessionKey &&
      selectableIdList.length > 0
    ) {
      setSelectionSessionKey(reportSessionKey);
      setSaveOnlySelected(true);
      setSelectedIds(new Set(selectableIdList));
    }
  }, [open, reportSessionKey, selectionSessionKey, report?.mode, selectableIdList]);

  if (!report || !summary) return null;

  const statusLabels = isPreview ? STATUS_LABEL : STATUS_LABEL_COMMITTED;
  const pendingSave = summary.pendingSave;
  const canSave = isPreview && (saveOnlySelected ? selectedCount > 0 : pendingSave > 0);
  const saveCount = saveOnlySelected ? selectedCount : pendingSave;
  const allVisibleSelected =
    visibleSelectableIds.length > 0 && visibleSelectableIds.every((id) => selectedIds.has(id));

  const toggleRow = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleSelectAllVisible = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const visibleSelectable = filteredRows
        .filter((r) => selectableIdSet.has(r.id))
        .map((r) => r.id);
      for (const id of visibleSelectable) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const filters: { key: FilterKey; label: string; count: number }[] = [
    { key: 'all', label: 'Tümü', count: report.rows.length },
    {
      key: 'updated',
      label: isPreview ? 'Kaydedilecek' : 'Güncellenen',
      count: summary.updated,
    },
    { key: 'skipped', label: 'Atlanan', count: summary.skipped },
    { key: 'unchanged', label: 'Değişmeyen', count: summary.unchanged },
    { key: 'failed', label: 'Hatalı', count: summary.failed },
    { key: 'no_selector', label: 'Seçici Olmayan', count: summary.noSelector },
    ...(suspiciousCount > 0
      ? [{ key: 'suspicious' as const, label: 'Şüpheli', count: suspiciousCount }]
      : []),
  ];

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />

        <div className="relative flex max-h-[92vh] w-full max-w-6xl flex-col rounded-xl bg-white shadow-xl dark:bg-gray-800">
          <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                {isPreview ? 'Toplu güncelleme önizlemesi' : 'Toplu güncelleme sonucu'}
              </Dialog.Title>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {report.finishedAt.toLocaleString('tr-TR')} · {report.total} forum raporda ·{' '}
                {report.scope === 'active' ? 'Sadece aktif' : 'Tüm forumlar'}
              </p>
              {isPreview && (
                <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                  Veritabanı henüz güncellenmedi. Kaydedilecek forumların tümü varsayılan olarak seçilidir;
                  istemediklerinizin işaretini kaldırın. İptal ederseniz mevcut veriler aynı kalır.
                </p>
              )}
              {suspiciousCount > 0 && (
                <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900 dark:border-amber-600/50 dark:bg-amber-950/40 dark:text-amber-100">
                  <AlertTriangle size={16} className="shrink-0" aria-hidden />
                  {suspiciousCount} forumda olağandışı büyük değişim var — kaydetmeden önce kontrol edin.
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700"
              aria-label="Kapat"
            >
              <X size={20} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 border-b border-gray-200 px-6 py-4 sm:grid-cols-3 lg:grid-cols-5 dark:border-gray-700">
            <SummaryCard
              icon={<CheckCircle2 className="text-green-500" size={20} />}
              label={isPreview ? 'Kaydedilecek' : 'Güncellenen'}
              value={summary.updated}
            />
            <SummaryCard
              icon={<MinusCircle className="text-amber-500" size={20} />}
              label="Atlanan"
              value={summary.skipped}
            />
            <SummaryCard
              icon={<AlertCircle className="text-gray-500" size={20} />}
              label="Değişmeyen"
              value={summary.unchanged}
            />
            <SummaryCard
              icon={<XCircle className="text-red-500" size={20} />}
              label="Hatalı"
              value={summary.failed}
            />
            <SummaryCard
              icon={<Slash className="text-violet-500" size={20} />}
              label="Seçici Olmayan"
              value={summary.noSelector}
            />
            {suspiciousCount > 0 && (
              <SummaryCard
                icon={<AlertTriangle className="text-amber-500" size={20} />}
                label="Şüpheli değişim"
                value={suspiciousCount}
                highlight
              />
            )}
          </div>

          <div className="flex flex-wrap gap-2 border-b border-gray-200 px-6 py-3 dark:border-gray-700">
            {filters.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                disabled={saving}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                  filter === f.key
                    ? f.key === 'suspicious'
                      ? 'bg-amber-600 text-white'
                      : 'bg-indigo-600 text-white'
                    : f.key === 'suspicious'
                      ? 'bg-amber-100 text-amber-900 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-100 dark:hover:bg-amber-900/60'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {f.label} ({f.count})
              </button>
            ))}
            {isPreview && (
              <label
                className="ml-auto inline-flex items-center gap-2 rounded-full bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                title="Açıldığında tüm kaydedilecek forumlar seçilidir; kaydetmek istemediklerinizin işaretini kaldırın."
              >
                <input
                  type="checkbox"
                  checked={saveOnlySelected}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSaveOnlySelected(checked);
                    if (checked && selectableIdList.length > 0) {
                      setSelectedIds(new Set(selectableIdList));
                    }
                  }}
                  disabled={saving || pendingSave === 0}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                />
                Sadece seçilenleri kaydet
              </label>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
            {filteredRows.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                Bu filtrede kayıt yok.
              </p>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-white text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  <tr>
                    {isPreview && (
                      <th className="pb-2 pr-3 font-medium">
                        <input
                          type="checkbox"
                          aria-label="Görünür kaydedilecek satırların hepsini seç"
                          checked={allVisibleSelected}
                          onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                          disabled={saving || visibleSelectableIds.length === 0}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                        />
                      </th>
                    )}
                    <th className="pb-2 pr-3 font-medium">Forum</th>
                    <th className="pb-2 pr-3 font-medium">Yazılım</th>
                    <th className="pb-2 pr-3 font-medium">Son güncelleme</th>
                    <th className="pb-2 pr-3 font-medium">Durum</th>
                    <th className="pb-2 pr-3 font-medium">Konu</th>
                    <th className="pb-2 pr-3 font-medium">İleti</th>
                    <th className="pb-2 pr-3 font-medium">Üye</th>
                    <th className="pb-2 font-medium">Sebep / not</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredRows.map((row) => {
                    const suspiciousRow = rowHasSuspiciousChanges(row);
                    return (
                    <tr
                      key={row.id}
                      className={`align-top ${
                        suspiciousRow
                          ? 'bg-amber-50/70 dark:bg-amber-950/20'
                          : ''
                      }`}
                    >
                      {isPreview && (
                        <td className="py-3 pr-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(row.id)}
                            onChange={(e) => toggleRow(row.id, e.target.checked)}
                            disabled={saving || !selectableIdSet.has(row.id)}
                            aria-label={`${row.name} satırını seç`}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 disabled:opacity-40"
                          />
                        </td>
                      )}
                      <td className="py-3 pr-3 font-medium text-gray-900 dark:text-white">
                        <span className="inline-flex items-center gap-1.5">
                          {suspiciousRow && (
                            <AlertTriangle
                              size={14}
                              className="shrink-0 text-amber-600 dark:text-amber-400"
                              aria-label="Şüpheli değişim"
                            />
                          )}
                          {row.name}
                        </span>
                      </td>
                      <td className="py-3 pr-3 text-xs text-gray-700 dark:text-gray-300">
                        {row.software || '—'}
                      </td>
                      <td className="whitespace-nowrap py-3 pr-3 text-xs text-gray-600 dark:text-gray-400">
                        {row.lastUpdatedLabel}
                      </td>
                      <td className="py-3 pr-3">
                        <span
                          className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[row.status]}`}
                        >
                          {statusLabels[row.status]}
                        </span>
                      </td>
                      <td className="py-3 pr-3 font-mono text-xs text-gray-700 dark:text-gray-300">
                        <StatChangeCell change={row.changes.find((c) => c.field === 'topics')} />
                      </td>
                      <td className="py-3 pr-3 font-mono text-xs text-gray-700 dark:text-gray-300">
                        <StatChangeCell change={row.changes.find((c) => c.field === 'posts')} />
                      </td>
                      <td className="py-3 pr-3 font-mono text-xs text-gray-700 dark:text-gray-300">
                        <StatChangeCell change={row.changes.find((c) => c.field === 'members')} />
                      </td>
                      <td className="max-w-xs break-words py-3 text-xs text-gray-600 dark:text-gray-400">
                        {row.reason}
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            {isPreview ? (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={saving}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  İptal et
                </button>
                <button
                  type="button"
                  onClick={() =>
                    onConfirmSave?.(
                      saveOnlySelected
                        ? Array.from(selectedIds).filter((id) => selectableIdSet.has(id))
                        : undefined
                    )
                  }
                  disabled={saving || !canSave}
                  className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                >
                  {saving && <Loader2 size={16} className="animate-spin" />}
                  {saving
                    ? 'Kaydediliyor...'
                    : saveCount > 0
                      ? `Kaydet (${saveCount})`
                      : 'Kaydedilecek kayıt yok'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Kapat
              </button>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

function SummaryCard({
  icon,
  label,
  value,
  highlight = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
        highlight
          ? 'border-amber-300 bg-amber-50 dark:border-amber-600/50 dark:bg-amber-950/40'
          : 'border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/50'
      }`}
    >
      {icon}
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        <p className="text-lg font-semibold text-gray-900 dark:text-white">{value}</p>
      </div>
    </div>
  );
}

export default BulkScrapeReportDialog;
